import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Calculator, DollarSign, Clock, TrendingUp } from 'lucide-react';
import type { TemplateScopeItem } from '../types';
import { sizingTotals } from '../utils/sizingEstimate';
import type { FixedBidQuote } from '../utils/fixedBidMarkup';
import { formatDollars } from '../utils/fixedBidMarkupText';

interface ScopingCalculatorProps {
  scopeItems: Array<{
    item: TemplateScopeItem;
    quantity: number | string;
  }>;
  hourlyRate?: number;
  showPricing?: boolean;
  compactMode?: boolean;
  /** A fixed bid: its base, markup and total take the place of hours × rate. */
  fixedBid?: FixedBidQuote | null;
}

export function ScopingCalculator({ 
  scopeItems, 
  hourlyRate = 150, 
  showPricing = true,
  compactMode = false,
  fixedBid = null,
}: ScopingCalculatorProps) {
  // The arithmetic lives in utils/sizingEstimate.ts, shared with the SOW generator's estimate.
  const totals = sizingTotals(scopeItems);
  const calculations = totals.lines.map((l) => ({ itemName: l.item.name, quantity: l.quantity, hoursEstimate: l.hoursEstimate, hoursMin: l.hoursMin, hoursMax: l.hoursMax, hasRange: l.hasRange }));
  const totalHoursEstimate = totals.hoursEstimate;
  const totalHoursMin = totals.hoursMin;
  const totalHoursMax = totals.hoursMax;
  const hasAnyRange = totals.hasRange;

  // Total price
  const totalPriceEstimate = totalHoursEstimate * hourlyRate;
  const totalPriceMin = totalHoursMin * hourlyRate;
  const totalPriceMax = totalHoursMax * hourlyRate;

  if (calculations.length === 0 || totalHoursEstimate === 0) {
    return null;
  }

  if (compactMode) {
    return (
      <div className="flex items-center gap-4 text-sm">
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4 text-gray-500" />
          <span className="font-medium">
            {hasAnyRange ? (
              `${Math.round(totalHoursMin)}-${Math.round(totalHoursMax)} hours`
            ) : (
              `${Math.round(totalHoursEstimate)} hours`
            )}
          </span>
        </div>
        {showPricing && (
          <div className="flex items-center gap-2">
            <DollarSign className="w-4 h-4 text-gray-500" />
            <span className="font-medium">
              {hasAnyRange ? (
                `$${Math.round(totalPriceMin).toLocaleString()} - $${Math.round(totalPriceMax).toLocaleString()}`
              ) : (
                `$${Math.round(totalPriceEstimate).toLocaleString()}`
              )}
            </span>
          </div>
        )}
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Calculator className="w-5 h-5 text-blue-600" />
              Scoping Estimate
            </CardTitle>
            <CardDescription>
              Effort and pricing estimates based on scope complexity
            </CardDescription>
          </div>
          {hourlyRate && (
            <Badge variant="outline">
              ${hourlyRate}/hour
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Breakdown */}
        <div className="space-y-2">
          {calculations.filter(calc => calc.quantity > 0).map((calc, idx) => (
            <div key={idx} className="flex items-center justify-between text-sm py-2 border-b border-gray-100 last:border-0">
              <div className="flex-1">
                <span className="text-gray-700">{calc.itemName}</span>
                <span className="text-gray-500 ml-2">×{calc.quantity}</span>
              </div>
              <div className="text-right">
                <div className="font-medium text-gray-900">
                  {calc.hasRange ? (
                    `${Math.round(calc.hoursMin)}-${Math.round(calc.hoursMax)} hrs`
                  ) : (
                    `${Math.round(calc.hoursEstimate)} hrs`
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Totals */}
        <div className="pt-4 border-t-2 border-gray-200 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-gray-700">
              <Clock className="w-4 h-4" />
              <span className="font-medium">Total Effort</span>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-gray-900">
                {hasAnyRange ? (
                  <span>{Math.round(totalHoursMin)}-{Math.round(totalHoursMax)}</span>
                ) : (
                  <span>{Math.round(totalHoursEstimate)}</span>
                )}
                <span className="text-lg text-gray-500 ml-1">hours</span>
              </div>
              {hasAnyRange && (
                <div className="text-xs text-gray-500 mt-1">
                  Est. {Math.round(totalHoursEstimate)} hours
                </div>
              )}
            </div>
          </div>

          {fixedBid && (
            <div className="flex items-center justify-between" data-testid="scoping-fixed-bid">
              <div className="flex items-center gap-2 text-gray-700">
                <DollarSign className="w-4 h-4" />
                <span className="font-medium">Fixed Bid</span>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-blue-600">{formatDollars(fixedBid.total)}</div>
                <div className="text-xs text-gray-500 mt-1">
                  {fixedBid.percent > 0
                    ? `Base ${formatDollars(fixedBid.base)} + ${fixedBid.percent}% markup = ${formatDollars(fixedBid.markup)}`
                    : 'No markup'}
                </div>
              </div>
            </div>
          )}

          {showPricing && (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-gray-700">
                <DollarSign className="w-4 h-4" />
                <span className="font-medium">Total Investment</span>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-blue-600">
                  {hasAnyRange ? (
                    <span>${Math.round(totalPriceMin / 1000)}k-${Math.round(totalPriceMax / 1000)}k</span>
                  ) : (
                    <span>${Math.round(totalPriceEstimate / 1000)}k</span>
                  )}
                </div>
                {hasAnyRange && (
                  <div className="text-xs text-gray-500 mt-1">
                    Est. ${Math.round(totalPriceEstimate).toLocaleString()}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Confidence Indicator */}
        <div className="pt-3 border-t border-gray-100">
          <div className="flex items-center gap-2 text-xs text-gray-600">
            <TrendingUp className="w-3 h-3" />
            <span>
              {hasAnyRange 
                ? 'Range-based estimate provides flexibility for scope variations'
                : 'Fixed estimate based on standard delivery patterns'
              }
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
